import { SlateNode, SlateUtils } from '@all-hands-hq/plex-graphql-schema';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Moment } from 'moment';
import React, { SyntheticEvent } from 'react';

import { PostType } from '../../../../graphql/gen/types';
import { colorPalette, spacings } from '../../../../theme';
import { Entities } from '../../../shared/slate/entities';
import { SlateEditor, SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { LabelInput } from '../../common/LabelInput';
import { CreateOrEditDialogTitle } from './CreateOrEditDialogTitle';
import { DateInput } from './DateInput';

type PostInputWithoutPublishStatus = {
  title: string;
  body: SlateNode[];
  primaryLinkId?: string | null;
  picture?: string | null;
  labelIds: string[];
};

type PostOrEventFormData = { type: PostType.Post } | { type: PostType.Event; eventDate: Moment | null };

export type PostFormData = {
  title: string;
  body: SlateEditorValue;
  entities: Entities;
  primaryLinkId: string | null | undefined;
  picture?: string;
  labelIds: string[];
  hasConcurrentUpdate: boolean;
} & PostOrEventFormData;

export function postFormDataToInput(data: PostFormData): PostInputWithoutPublishStatus {
  const { links, pictures } = SlateUtils.accumulateStateFromNodes(data.body.value);
  const primaryLinkId = SlateUtils.primaryLink({ links, primaryLinkId: data.primaryLinkId });
  return {
    title: data.title,
    labelIds: data.labelIds,
    body: data.body.value,
    picture: pictures[0],
    primaryLinkId,
  };
}

type Props = {
  dialogTitle: string;
  post: PostFormData;
  onPostChange: (post: PostFormData) => void;
};

export function PostDialogContent({ dialogTitle, post, onPostChange }: Props): React.ReactElement {
  const classes = useStyles();

  const handleTitleChange = (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    onPostChange({ ...post, title: event.currentTarget.value });
  };

  const handleBodyChange = (value: SlateEditorValue): void => {
    onPostChange({ ...post, body: value });
  };

  const handleChangePrimaryLink = (primaryLinkId: string): void => {
    onPostChange({ ...post, primaryLinkId });
  };

  const handleChangeLabelIds = (labelIds: string[]): void => {
    onPostChange({ ...post, labelIds });
  };

  const handleEventDateChange = (moment: Moment | null): void => {
    if (post.type === PostType.Event) {
      onPostChange({ ...post, eventDate: moment });
    }
  };

  return (
    <React.Fragment>
      <CreateOrEditDialogTitle />

      <div className={classes.topInputsContainer}>
        <TextField
          type={'text'}
          value={post.title}
          margin={'dense'}
          label={'Title'}
          onChange={handleTitleChange}
          className={classes.plexTitleInputContainer}
          InputProps={{ classes: { input: classes.plexTitleInput } }}
        />
        {post.type === PostType.Event && (
          <DateInput
            value={post.eventDate}
            onChange={handleEventDateChange}
            InputProps={{ classes: { input: classes.plexTitleInput } }}
          />
        )}
        <LabelInput
          className={classes.labelInput}
          selectedLabelIds={post.labelIds}
          onLabelsListChanged={handleChangeLabelIds}
        />
      </div>

      <SlateEditor
        value={post.body}
        initialEntities={post.entities}
        className={classes.richTextBody}
        onChange={handleBodyChange}
        primaryLink={post.primaryLinkId}
        onChangePrimaryLink={handleChangePrimaryLink}
      />
    </React.Fragment>
  );
}

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 120,
  },
  linkForm: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  urlField: {
    width: '95%',
  },
  topInputsContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: spacings.xlarge,
    marginRight: spacings.xlarge,
    flexShrink: 0,
    // Set spacing between consecutive children
    '& > * + *': {
      marginLeft: spacings.xlarge,
    },
  },
  plexTitleInputContainer: {
    flex: 1,
  },
  plexTitleInput: {
    height: 30,
  },
  labelInput: {
    flex: 1,
  },
  richTextBody: {
    marginTop: spacings.small,
    borderTop: `1px solid ${colorPalette.gray._300}`,
  },
}));
